import { insightDetailRoute } from "../../../config/insightDetails.js";
import {
  INSIGHTS_PAGE_SIZE,
  getFilteredInsights,
  getInsightServiceTags,
  insightsListHref,
  insightsPage,
  paginateInsights,
  parseInsightsQuery,
} from "../../../config/insightsContent.js";
import { buildAnimatedHeading } from "../../HeroTitleAnimation.js";
import { buildSectionPillRow } from "../../components/SectionPill.js";

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/"/g, "&quot;");
}

function renderCard(item, index) {
  const href = insightDetailRoute(item.slug);
  const accent = item.accent || "#6c8cff";
  const indexLabel = String((index % 99) + 1).padStart(2, "0");

  return `
    <a
      class="services-insight"
      href="${href}"
      style="--card-i: ${index}; --insight-accent: ${accent}"
      aria-label="Read insight: ${escapeHtml(item.title)}"
    >
      <div class="services-insight__media" aria-hidden="true">
        <span class="services-insight__index">${indexLabel}</span>
        <span class="services-insight__orbit"></span>
        <span class="services-insight__orbit services-insight__orbit--b"></span>
      </div>
      <div class="services-insight__body">
        <div class="services-insight__meta">
          <span class="services-insight__category">${escapeHtml(item.serviceLabel)}</span>
          <span class="services-insight__time">${escapeHtml(item.readTime)}</span>
        </div>
        <h3 class="services-insight__title">${escapeHtml(item.title)}</h3>
        <p class="services-insight__excerpt">${escapeHtml(item.excerpt)}</p>
        <span class="services-insight__more">
          Open insight
          <span aria-hidden="true">→</span>
        </span>
      </div>
    </a>
  `;
}

function renderTags(activeService) {
  return getInsightServiceTags()
    .map((tag) => {
      const isOn = tag.slug === activeService;
      const href =
        isOn && tag.slug
          ? insightsListHref({ service: "", page: 1 })
          : insightsListHref({ service: tag.slug, page: 1 });
      return `
        <a
          class="insights-feed__tag${isOn ? " is-on" : ""}"
          href="${href}"
          data-service="${escapeHtml(tag.slug)}"
          role="listitem"
          ${isOn ? 'aria-current="true"' : ""}
        >${escapeHtml(tag.label)}</a>
      `;
    })
    .join("");
}

function renderPager(service, page, pageCount) {
  if (pageCount <= 1) return "";

  const pages = Array.from({ length: pageCount }, (_, i) => i + 1);
  const prevHref = insightsListHref({ service, page: Math.max(1, page - 1) });
  const nextHref = insightsListHref({ service, page: Math.min(pageCount, page + 1) });

  return `
    <nav class="insights-feed__pager" aria-label="Insights pagination">
      <a
        class="insights-feed__page${page <= 1 ? " is-disabled" : ""}"
        href="${prevHref}"
        data-page="${Math.max(1, page - 1)}"
        ${page <= 1 ? 'aria-disabled="true" tabindex="-1"' : ""}
      >Previous</a>
      <ol class="insights-feed__pages">
        ${pages
          .map(
            (n) => `
          <li>
            <a
              class="insights-feed__page${n === page ? " is-on" : ""}"
              href="${insightsListHref({ service, page: n })}"
              data-page="${n}"
              ${n === page ? 'aria-current="page"' : ""}
            >${n}</a>
          </li>
        `
          )
          .join("")}
      </ol>
      <a
        class="insights-feed__page${page >= pageCount ? " is-disabled" : ""}"
        href="${nextHref}"
        data-page="${Math.min(pageCount, page + 1)}"
        ${page >= pageCount ? 'aria-disabled="true" tabindex="-1"' : ""}
      >Next</a>
    </nav>
  `;
}

function currentState() {
  return parseInsightsQuery(window.location.search);
}

export function createInsightsFeedSection() {
  const { feed } = insightsPage;
  const section = document.createElement("section");
  section.id = "library";
  section.className = "section insights-feed";

  const paint = () => {
    const { service, page } = currentState();
    const filtered = getFilteredInsights(service);
    const paged = paginateInsights(filtered, page);
    const startIndex = (paged.page - 1) * INSIGHTS_PAGE_SIZE;
    const from = paged.total === 0 ? 0 : startIndex + 1;
    const to = startIndex + paged.items.length;
    const rangeLabel =
      paged.total === 0
        ? "No insights in this lane yet."
        : `Showing ${from}–${to} of ${paged.total}${service ? " in this lane" : ""}`;

    const grid =
      paged.items.length > 0
        ? paged.items.map((item, index) => renderCard(item, startIndex + index)).join("")
        : `<p class="insights-feed__empty">No insights in this lane yet. Choose All to see every note.</p>`;

    section.querySelector("[data-insights-body]").innerHTML = `
      <div class="insights-feed__tags" role="list">${renderTags(service)}</div>
      <p class="insights-feed__count">${rangeLabel}</p>
      <div class="insights-feed__grid">${grid}</div>
      ${renderPager(service, paged.page, paged.pageCount)}
    `;
  };

  section.innerHTML = `
    <div class="section__inner insights-feed__inner">
      <header class="insights-feed__intro reveal">
        ${buildSectionPillRow(feed.badge)}
        <h2 class="insights-feed__title">${buildAnimatedHeading(feed.titleSegments)}</h2>
        <p class="insights-feed__subtitle">${feed.subtitle}</p>
      </header>
      <div data-insights-body></div>
    </div>
  `;

  paint();

  section.addEventListener("click", (event) => {
    const link = event.target.closest("[data-service], [data-page]");
    if (!link || !section.contains(link) || link.getAttribute("aria-disabled") === "true") return;

    event.preventDefault();

    const activeService = currentState().service;
    const nextService = link.hasAttribute("data-service")
      ? link.getAttribute("data-service") || ""
      : activeService;
    const toggledService =
      link.hasAttribute("data-service") && nextService && nextService === activeService
        ? ""
        : nextService;
    const nextPage = link.hasAttribute("data-page")
      ? Number.parseInt(link.getAttribute("data-page") || "1", 10)
      : 1;

    history.pushState(
      null,
      "",
      insightsListHref({
        service: toggledService,
        page: link.hasAttribute("data-page") ? nextPage : 1,
      })
    );
    paint();
    section.scrollIntoView({ behavior: "smooth", block: "start" });
  });

  window.addEventListener("popstate", paint);

  return section;
}
